import { RefObject, useEffect } from 'react';

interface UseOutsideEventProps {
  ref: RefObject<HTMLElement>;
  isOpen: boolean;
  onOutsideClick: (e: MouseEvent | KeyboardEvent | TouchEvent) => void;
  ignoreRef?: RefObject<HTMLElement>;
}

/**
 * Hook to detect clicks outside a specific element
 */
export function useOutsideEvent({
  ref,
  isOpen,
  onOutsideClick,
  ignoreRef,
}: UseOutsideEventProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleOutsideEvent = (e: MouseEvent | KeyboardEvent | TouchEvent) => {
      // Only handle Tab key for keyboard events
      if ('key' in e && !['Tab', 'Escape'].includes(e.key)) return;

      // For Escape key, always close
      if ('key' in e && e.key === 'Escape') {
        onOutsideClick(e);
        return;
      }

      // Handle click outside
      if (
        isOpen &&
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        (!ignoreRef?.current || !ignoreRef.current.contains(e.target as Node))
      ) {
        onOutsideClick(e);
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleOutsideEvent);
    document.addEventListener('touchmove', handleOutsideEvent);
    document.addEventListener('keydown', handleOutsideEvent);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleOutsideEvent);
      document.removeEventListener('touchmove', handleOutsideEvent);
      document.removeEventListener('keydown', handleOutsideEvent);
    };
  }, [isOpen, onOutsideClick, ref, ignoreRef]);
}
